/**
 * Every tunable constant in the forecast animation, in one place.
 *
 * These started with the v89 prototype values; narrowly scoped visual updates
 * (such as the inland chop spectrum) live here too. Keeping them together and
 * out of the draw code is deliberate: tuning the look should never mean hunting
 * through the render loop.
 */

/** Acceleration due to gravity, m/s². */
export const GRAVITY = 9.81;

/* ---------- hull response ---------- */

/** Pitch gain: best-fit wave slope under the hull → tilt attitude. */
export const PITCH_GAIN = 0.7;

/** Pitch damping. Curbs the resonance overshoot. */
export const PITCH_DAMPING = 0.3;

/**
 * Heave damping. Radiation-damped, so the hull lags the surface without
 * building a big resonant response.
 */
export const HEAVE_DAMPING = 0.6;

/** Capsize roll stiffness. Gentle roll-over rather than a slam. */
export const CAPSIZE_STIFFNESS = 3.6;

/** Capsize damping. High enough to ease to inverted without a hard stop. */
export const CAPSIZE_DAMPING = 0.72;

/** Pitch is clamped to this magnitude, radians. */
export const PITCH_LIMIT_RAD = 0.55;

/** Number of surface samples taken across the hull footprint. */
export const HULL_SAMPLE_COUNT = 7;

/** Physics runs on a fixed sub-step, seconds. */
export const PHYSICS_STEP_S = 1 / 120;

/** Never run more than this many sub-steps in one frame. */
export const PHYSICS_MAX_STEPS = 16;

/** A frame longer than this is discarded rather than integrated. */
export const MAX_FRAME_DT_S = 0.05;

/* ---------- sea spectrum ---------- */

/**
 * Spectral slots: a swell band and a wind-sea band. `mult` scales the peak
 * period for that slot; `weight` is its share of the band's energy.
 */
export const SPECTRUM_SLOTS = [
  { kind: 'swell', mult: 0.82, weight: 0.5 },
  { kind: 'swell', mult: 0.91, weight: 0.85 },
  { kind: 'swell', mult: 1.0, weight: 1.0 },
  { kind: 'swell', mult: 1.12, weight: 0.8 },
  { kind: 'swell', mult: 1.28, weight: 0.45 },
  { kind: 'wind', mult: 0.8, weight: 0.55 },
  { kind: 'wind', mult: 1.0, weight: 0.9 },
  { kind: 'wind', mult: 1.25, weight: 0.55 },
] as const;

/**
 * Inland wind sea has no swell band, so the original slots otherwise collapse
 * around one peak period and read as regular rollers. This wider equilibrium-
 * range-like spread puts short chop over the longer waves without changing the
 * component count or significant height.
 */
export const INLAND_SPECTRUM_SLOTS = [
  { mult: 0.55, weight: 0.34 },
  { mult: 0.66, weight: 0.46 },
  { mult: 0.78, weight: 0.66 },
  { mult: 0.9, weight: 0.88 },
  { mult: 1.0, weight: 1.0 },
  { mult: 1.12, weight: 0.84 },
  { mult: 1.26, weight: 0.58 },
  { mult: 1.42, weight: 0.36 },
] as const;

/** Wind range over which the broader inland chop spectrum fades in. */
export const INLAND_CHOP_START_MPS = 5 / 1.94384;
export const INLAND_CHOP_FULL_MPS = 9 / 1.94384;

/** Fixed phase offsets per slot, so the sea looks the same on every load. */
export const SPECTRUM_PHASES = [0.0, 1.9, 3.6, 5.1, 0.7, 2.6, 4.4, 5.8];

/**
 * Uniform steepness exaggeration: screen steepness = STEEPNESS_EXAGGERATION ×
 * real steepness. Waves stay correctly ordered (long swell gentler and faster,
 * short chop steeper) but read legibly in a 166px-tall canvas.
 */
export const STEEPNESS_EXAGGERATION = 5;

/** Original visual wavelength floor, retained for coast and calm inland water. */
export const WAVELENGTH_MIN_PX = 24;

/**
 * Full-chop inland floor. At the full-quality 2px sample step this still leaves
 * nine samples per wavelength. It fades in with the spectrum, preserving v89
 * exactly below the chop threshold.
 */
export const INLAND_WAVELENGTH_MIN_PX = 18;

/** Draw-time inland judder tuning. It never feeds hull physics or spray. */
export const JUDDER_MAX_PX = 3.4;
export const JUDDER_HULL_FRACTION = 0.0408;
export const JUDDER_ROTATION_PER_PX = 0.0035;

/** How quickly the residual-driven inland chop level eases, per second. */
export const HULL_CHOP_EASE_RATE = 4;

/** Extra waterline churn at full inland judder. */
export const HULL_CHOP_FOAM_GAIN = 0.14;

/** Significant height floor, px, so a flat calm still shows a ripple. */
export const SIGNIFICANT_HEIGHT_MIN_PX = 2.4;

/**
 * Stokes-like crest shaping strength. A raw sine sum is symmetric, which is the
 * big synthetic tell; this pushes crests up and flattens troughs while
 * preserving the mean.
 */
export const CREST_SHAPING = 0.3;

/* ---------- canvas geometry ---------- */

/** Horizon sits this far down the canvas. */
export const HORIZON_FRACTION = 0.6;

/** Waves may draw to this fraction of the canvas height before clipping. */
export const WAVE_CLAMP_FRACTION = 0.95;

/** The boat's own vertical band, as a fraction of canvas height. */
export const HEAVE_LIMIT_FRACTION = 0.4;

/** Hull draw width in px for a vessel of length L: HULL_PX_BASE + L × HULL_PX_PER_M. */
export const HULL_PX_BASE = 33;
export const HULL_PX_PER_M = 4.3;
export const HULL_PX_MIN = 40;

/** The vessel artwork is authored at this width, so draws scale by hullPx / this. */
export const HULL_ART_WIDTH = 86;

/* ---------- weather backdrop ---------- */

/** Cloud cover target per sky state. */
export const CLOUD_TARGET = { sunny: 0, cloudy: 0.85, rain: 0.95, storm: 1 };

/** Rain intensity target per sky state. */
export const RAIN_TARGET = { sunny: 0, cloudy: 0, rain: 0.7, storm: 0.95 };

/** Storm (lightning) target per sky state. */
export const STORM_TARGET = { sunny: 0, cloudy: 0, rain: 0, storm: 1 };

/** Backdrop cross-fade rate. Roughly a 1.5s glide between sky states. */
export const SKY_EASE_RATE = 0.9;

/** Number of drifting cloud puffs. */
export const CLOUD_COUNT = 5;

/** Number of wind streaks in the sky. */
export const STREAK_COUNT = 16;

/** Number of stars in the fixed night field. */
export const STAR_COUNT = 42;

/**
 * Streak colour at full daylight. Blends from white at night (so streaks read
 * against a dark sky) to this soft blue-grey by day — a touch darker than the
 * midday sky so they stay visible without turning into harsh lines.
 */
export const STREAK_DAY_RGB = [136, 164, 184];

/**
 * Raindrop colour at full daylight. A muted blue-grey keeps rain legible against
 * the overcast sky gradient without making it the dominant part of the scene.
 */
export const RAIN_DAY_RGB = [126, 145, 164];

/** Raindrop colour at night — pale so drops read against the dark sky. */
export const RAIN_NIGHT_RGB = [220, 232, 244];

/* ---------- particles ---------- */

/** Foam patch drift, px per second, as the crest moves on and leaves it behind. */
export const FOAM_DRIFT_PX_S = 18;

/** Gravity on spindrift, px/s². Light, so it hangs and blows downwind. */
export const SPINDRIFT_GRAVITY = 120;

/** Gravity on slam spray, px/s². Heavy, so it arcs and falls back. */
export const SPRAY_GRAVITY = 520;

/** Bow clearance under which a slam can trigger, px. */
export const SLAM_GAP_PX = 8;

/** Bow closing speed above which a slam triggers, px/s. */
export const SLAM_CLOSING_PX_S = 140;

/** Number of spray particles thrown when the hull goes over. */
export const CAPSIZE_SPLASH_COUNT = 16;

/* ---------- adaptive quality ladder ---------- */

/**
 * One-way detail step-down. Level 0 is full detail; the ladder only ever steps
 * down, never back up, so a device that struggles settles rather than
 * oscillating between levels.
 *
 * `dprCap` is 2 even at full quality — the canvas is only ~166px tall, so 2.5x
 * buys nothing visible.
 */
export const QUALITY_LEVELS = [
  { dprCap: 2, stepX: 2, foamMax: 260, spindriftMax: 140, particleMax: 90 },
  { dprCap: 1.5, stepX: 3, foamMax: 140, spindriftMax: 60, particleMax: 60 },
  { dprCap: 1.25, stepX: 4, foamMax: 80, spindriftMax: 0, particleMax: 40 },
] as const;

/** Frame time above which a frame counts as slow, seconds (~48fps). */
export const SLOW_FRAME_S = 0.021;

/** Consecutive slow frames before the ladder steps down a level. */
export const SLOW_FRAMES_BEFORE_STEP_DOWN = 60;

/** Smoothing on the frame-time average that feeds the ladder. */
export const FRAME_TIME_EMA_ALPHA = 0.05;
